import React, { useState } from 'react';
import { FileText, Trash2, Download, Pencil } from 'lucide-react';
import Swal from 'sweetalert2';
import useApi from "../../utils/useApi";
import IdEditModal from "./IdEditModal";

const IdDisplay = ({ user, client, documents, onRefresh, idExpiry = "", idType = 'id', spouseIdExists, filingStatus }) => {
    const { deleteId } = useApi();
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedDoc, setSelectedDoc] = useState(null);

    const isSpouseId = idType === 'spouse_id';
    const labelText = isSpouseId ? 'Spouse Identification' : 'Identification';
    const expirationField = isSpouseId ? 'spouse_identification_expiration_date' : 'identification_expiration_date';
    const noDocsMessage = isSpouseId ? 'No spouse identification documents uploaded' : 'No identification documents uploaded';

    const formatFileSize = (bytes) => {
        if (bytes === 0 || !bytes) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleDelete = async (document) => {
        console.log(document);
        const docLabel = isSpouseId ? 'Spouse ID' : 'ID';
        try {
            const result = await Swal.fire({
                                               title: 'Are you sure?',
                                               text: `This ${docLabel} document will be permanently deleted!`,
                                               icon: 'warning',
                                               showCancelButton: true,
                                               confirmButtonColor: '#dc3545',
                                               cancelButtonColor: '#6c757d',
                                               confirmButtonText: 'Yes, delete it!',
                                               cancelButtonText: 'Cancel'
                                           });

            if (result.isConfirmed) {
                console.log(client);
                const response = await deleteId(client.id, document, idType);

                if (response.success) {
                    onRefresh();
                    await Swal.fire({
                                        title: 'Deleted!',
                                        text: `${docLabel} document has been deleted.`,
                                        icon: 'success',
                                        confirmButtonColor: '#198754'
                                    });
                } else {
                    // handle failed delete here
                }
            }
        } catch (error) {
            await Swal.fire({
                                title: 'Error',
                                text: error.message || `Failed to delete ${docLabel} document`,
                                icon: 'error',
                                confirmButtonColor: '#dc3545'
                            });
        }
    };

    // This function will programmatically trigger a download
    const handleDownload = (doc) => {
        // Create a temporary link and click it to initiate download
        const link = document.createElement('a');
        link.href = doc.url;
        link.target = '_blank';
        link.download = doc.file_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const renderFileContent = (doc) => {
        if (doc.url) {
            if (doc.type.includes('pdf')) {
                return (
                    <div className="w-full h-48 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                        <FileText className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                        <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">PDF Document</span>
                    </div>
                );
            }
            return (
                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                    <img
                        src={doc.url}
                        alt={doc.file_name}
                        className="w-full max-h-64 object-contain"
                    />
                </div>
            );
        }

        return (
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                    {doc.type.includes('pdf') ? 'PDF' : doc.type.includes('image') ? 'IMG' : 'DOC'}
                </span>
            </div>
        );
    };

    if (!documents || documents.length === 0) {
        return (
            <div id="id-display" className="p-4 text-center text-gray-500 dark:text-gray-400">
                {noDocsMessage}
            </div>
        );
    }

    return (
        <div id="id-display" className="flex-1 min-w-[200px]">
            <h2 className="text-lg mb-3">
                {labelText}
            </h2>

            <div className="flex flex-wrap gap-4">
                {documents.map((doc, index) => (
                    <div
                        key={doc.id || index}
                        className={`flex flex-col p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm ${spouseIdExists ? 'w-full':''}`}
                    >
                        {renderFileContent(doc)}

                        <div className="mt-4">
                            <div className="flex justify-between items-start">
                                <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                    {doc.file_name}
                                </h3>
                                <div className="flex space-x-2">
                                    {/* Edit Button */}
                                    <button
                                        onClick={() => { setSelectedDoc(doc); setEditModalOpen(true); }}
                                        className="p-1 text-gray-500 hover:text-yellow-500 dark:text-gray-400 dark:hover:text-yellow-400 transition-colors"
                                        title="Edit document"
                                    >
                                        <Pencil className="w-4 h-4" />
                                    </button>

                                    {/* Download Button */}
                                    <button
                                        onClick={() => handleDownload(doc)}
                                        className="p-1 text-gray-500 hover:text-blue-500 dark:text-gray-400 dark:hover:text-blue-400 transition-colors"
                                        title="Download document"
                                    >
                                        <Download className="w-4 h-4" />
                                    </button>

                                    {/* Delete Button */}
                                    <button
                                        onClick={() => handleDelete(doc)}
                                        className="p-1 text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 transition-colors"
                                        title="Delete document"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                            <div className="mt-1 space-y-1">
                                <div className="flex justify-between">
                                    <span className="text-xs text-gray-500 dark:text-gray-400">Expiry Date</span>
                                    <span className="text-xs text-gray-700 dark:text-gray-300">{client.profile?.[expirationField] ?? idExpiry}</span>
                                </div>
                                {user.user_type === 'admin' && (
                                    <>
                                        <div className="flex justify-between">
                                            <span className="text-xs text-gray-500 dark:text-gray-400">Type</span>
                                            <span className="text-xs text-gray-700 dark:text-gray-300">{doc.type}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-xs text-gray-500 dark:text-gray-400">Size</span>
                                            <span className="text-xs text-gray-700 dark:text-gray-300">{formatFileSize(doc.size)}</span>
                                        </div>
                                    </>
                                )}

                                <div className="flex justify-between">
                                    <span className="text-xs text-gray-500 dark:text-gray-400">Uploaded</span>
                                    <span className="text-xs text-gray-700 dark:text-gray-300">{formatDate(doc.created_at)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <IdEditModal
                isOpen={editModalOpen}
                onClose={() => { setEditModalOpen(false); setSelectedDoc(null); }}
                onSave={onRefresh}
                document={selectedDoc}
                client={client}
                currentIdType={idType}
                idExpiry={client?.profile?.[expirationField] ?? idExpiry}
                showAssignmentOption={filingStatus === 'Married Filing Joint Return'}
            />
        </div>
    );
};

export default IdDisplay;
